import { EventEmitter, Injectable, OnDestroy, OnInit } from "@angular/core"
import { Environment } from "environments/environment"
import { BehaviorSubject, interval, Subject, Subscription } from "rxjs"
import { webSocket, WebSocketSubject } from "rxjs/webSocket"
import { StateService } from "app/shared/services/state.service"
import { STORAGE_TOKEN, STORAGE_USER } from "../../app-settings"
import { Employee } from "../models/employee"
import { Hotel } from "../models"
import { LogService } from "./error-log.service"

const AUTH_TIMEOUT_MS = 20000

const CLOSE_CODE_DESCRIPTIONS: Record<number, string> = {
  1000: "Normal closure",
  1001: "Going away",
  1006: "Abnormal closure (network, proxy, firewall or server unreachable)",
  1008: "Policy violation",
  1011: "Server error",
  1015: "TLS handshake failed",
}

@Injectable()
export class CommunicationWebSocketService {
  windowHasFocus = true
  socket: WebSocket
  webSocketIsActivated = false
  private authorized = false
  private failureLogged = false
  private connectStartedAt = 0
  private authTimeout: ReturnType<typeof setTimeout>

  CommunicationWebSocketMessageEventEmitter: EventEmitter<any> = new EventEmitter<any>()
  CommunicationBuilderWebSocketEventEmitter: EventEmitter<any> = new EventEmitter<any>()
  CommunicationAutomationWebSocketEventEmitter = new Subject<any>()
  constructor(private stateService: StateService, private logService: LogService) {
    this.initCommunicationWebSocket()

    window.addEventListener("focus", (event) => {
      this.windowHasFocus = true
      this.initCommunicationWebSocket()
    })

    window.addEventListener("beforeunload", (event) => {
      this.disconnect()
    })
  }
  refreshCommunicationWebSocket() {
    this.disconnect()
    this.initCommunicationWebSocket()
  }

  initCommunicationWebSocket() {
    const user: Employee = JSON.parse(localStorage.getItem(STORAGE_USER))
    const token = localStorage.getItem(STORAGE_TOKEN)?.split(",")[0]
    if (!this.socket || this.socket === null) {
      this.resetConnectionTracking()
      try {
        this.socket = new WebSocket(Environment.WebsocketGateCommunication)
      } catch (error) {
        this.logConnectionFailure("Could not create websocket", { error: String(error) })
        return
      }
      const socket = this.socket
      this.authTimeout = setTimeout(() => {
        if (this.socket === socket && !this.authorized) this.logConnectionFailure(`No authorization response within ${AUTH_TIMEOUT_MS / 1000} seconds`, { readyState: socket.readyState })
      }, AUTH_TIMEOUT_MS)
      this.socket.onopen = (event) => {
        this.sendMessage({ protocol: "json", version: 1 })
        var authObj = {
          type: 1,
          target: "AuthorizeReception",
          arguments: [token, this.stateService.selectedHotel.Id],
        }
        setTimeout(() => {
          this.sendMessage(authObj)
        }, 100)
      }
      this.socket.onmessage = (event) => {
        var cleanedData = event.data.replace(/\u001e/g, "") // Remove record separator
        const msg = this.isJSONString(cleanedData) ? JSON.parse(cleanedData) : null
        if (event.data === '{"type":6}\u001e') {
        } else if (msg?.target === "ReceiveMessage" && msg?.type === 1) {
          this.CommunicationWebSocketMessageEventEmitter.emit(msg)
        } else if (msg?.target === "BuilderAccess" && msg?.type === 1) {
          this.CommunicationBuilderWebSocketEventEmitter.emit(msg)
        } else if (msg?.target === "AutomationRequest" && msg?.type === 1) {
          this.CommunicationAutomationWebSocketEventEmitter.next(msg)
        } else if (msg?.target === "AuthSuccess" && (msg?.type === 1 || msg?.type === "message")) {
          this.webSocketIsActivated = true
          this.authorized = true
          clearTimeout(this.authTimeout)
          this.CommunicationWebSocketMessageEventEmitter.emit("Connection established")
          this.CommunicationBuilderWebSocketEventEmitter.emit("Connection established")
          this.CommunicationAutomationWebSocketEventEmitter.next("Connection established")
        } else if (msg?.target === "AuthFailure" && msg?.type === 1) {
          this.CommunicationWebSocketMessageEventEmitter.emit("Connection failed")
          console.error("Websocket Auth failed")
          this.logConnectionFailure("Authorization rejected by server", {})
          this.socket.close()
        }
      }
      this.socket.onclose = (event) => {
        if (this.socket !== socket || this.authorized) return
        this.logConnectionFailure(`Closed before authorization - ${this.describeCloseCode(event.code)}`, { closeCode: event.code, closeReason: event.reason, wasClean: event.wasClean })
      }
    }
  }

  private resetConnectionTracking(): void {
    clearTimeout(this.authTimeout)
    this.authorized = false
    this.failureLogged = false
    this.connectStartedAt = Date.now()
  }

  private describeCloseCode(code: number): string {
    return CLOSE_CODE_DESCRIPTIONS[code] ?? `Close code ${code}`
  }

  private logConnectionFailure(reason: string, details: Record<string, unknown>): void {
    if (this.failureLogged) return
    this.failureLogged = true
    clearTimeout(this.authTimeout)
    const metrics = {
      ...details,
      hasToken: !!localStorage.getItem(STORAGE_TOKEN),
      online: navigator.onLine,
      elapsedMs: Date.now() - this.connectStartedAt,
      url: Environment.WebsocketGateCommunication,
    }
    this.logService.logAnonymousError(`Websocket connection failed: ${reason}`, metrics)
  }

  private isJSONString(str) {
    try {
      const parsed = JSON.parse(str)
      return typeof parsed === "object" && parsed !== null
    } catch (e) {
      return false
    }
  }

  sendMessage(msg: any): void {
    const payload = JSON.stringify(msg)
    const messageWithSeparator = `${payload}\u001e` // Record separator
    this.socket.send(messageWithSeparator)
  }
  disconnect(): void {
    this.socket?.close()
    this.socket = null
  }
}
